import React, { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ESG_ICONS } from '../../constants/esgIcons';
import type { WasteTypeWithMaterials } from '../../types/dashboard.type';
import { blueRamp } from '../../theme/chartTheme';
import { flattenRecycledWasteForChart } from '../../utils/recycledWasteChart.util';
import ChartCard from './ChartCard';

interface EsgRecycledWasteByTypeChartProps {
  rows: WasteTypeWithMaterials[];
  title: string;
  chartType: 'bar' | 'line' | 'area' | 'pie';
  onMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  caption?: React.ReactNode;
  id?: string;
}

/**
 * Collected-waste composition by type and material. Pie view renders a thin
 * donut with a ranked list (or the list alone when there are many segments).
 * Materials are ranked largest first and coloured with one blue at decreasing
 * strength, so the biggest share always carries the strongest blue.
 */
const EsgRecycledWasteByTypeChart: React.FC<EsgRecycledWasteByTypeChartProps> = ({
  rows,
  title,
  chartType,
  onMenuClick,
  caption,
  id,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const flat = useMemo(
    () =>
      flattenRecycledWasteForChart(rows, {
        other: t('carbonAvoidance.other'),
        remainder: t('carbonAvoidance.otherRemainder'),
      }).sort((a, b) => b.total_kg - a.total_kg),
    [rows, t],
  );
  const colors = useMemo(() => blueRamp(theme, flat.length), [theme, flat.length]);

  return (
    <ChartCard
      id={id}
      title={title}
      icon={ESG_ICONS.materialFlows}
      caption={caption}
      data={flat}
      type={chartType}
      dataKey="total_kg"
      nameKey="waste_type"
      valueKind="mass_kg"
      unit={t('units.kg')}
      seriesLabel={t('units.kg')}
      segmentColors={colors}
      onMenuClick={onMenuClick}
    />
  );
};

export default EsgRecycledWasteByTypeChart;
